/**
 * Activity Detail — view a single recorded outing with stats and charts.
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import '@material/web/progress/linear-progress.js'
import '@material/web/switch/switch.js'
import { db } from '../../database/schema.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { activityStorage } from '../../storage/activity-storage.js'
import {
  associateActivityWithPlan,
  unassociateActivityFromPlan,
} from '../../services/plan-association-service.js'
import {
  detectTrackGaps,
  DEFAULT_MIN_GAP_METERS,
  type TrackGap,
} from '../../services/track/track-gaps.js'
import {
  fillGap,
  splitTrack,
  assembleTrack,
  filledGapStartIndices,
  stripLegacyInterpolation,
} from '../../services/track/track-edit.js'
import { syncChildrenOnNavigate, forcePushEntity } from '../../storage/sync-manager.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { router } from '../../utils/router.js'
import '../common/confirm-dialog.js'
import type { ConfirmDialog } from '../common/confirm-dialog.js'
import type { Activity } from '../../schemas/activity.js'
import type { Feature } from '../../schemas/feature.js'
import type { Plan } from '../../schemas/plan.js'
import type { Route } from '../../schemas/route.js'
import type { UnitSystem } from '../../utils/units.js'
import { convertDistance, convertElevation } from '../../utils/units.js'
import '../map/elevation-profile.js'
import '../map/distance-time-chart.js'
import './activity-track-map.js'
import type {
  ProfilePoint,
  NamedProfile,
  ElevationProfile,
  RestPeriod,
} from '../map/elevation-profile.js'
import type { DistanceTimePoint, DistanceTimeChart } from '../map/distance-time-chart.js'
import { processTrack } from '../../services/track/track-processing-service.js'
import { computeUncertaintyCorridor } from '../../services/track/uncertainty-corridor.js'
import {
  computeElevationStats,
  computeElevationProfile,
} from '../../services/track/elevation-metrics.js'
import type { ProcessedTrack } from '../../services/track/track-processing-service.js'
import { haversineDistance } from '../map/geo-utils.js'
import { reverseGeocodeService } from '../../services/geocode/reverse-geocode-service.js'

interface PaceSplit {
  /** Split index (0-based) */
  index: number
  /** Distance covered in this split, meters */
  distance: number
  /** Time spent in this split, seconds */
  duration: number
  /** Elevation gain in this split, meters */
  gain: number
  /** True when most of this split is synthetic gap-fill (pace is estimated). */
  interpolated: boolean
}

interface SlopeBin {
  label: string
  /** Total distance traveled in this bin (meters) */
  distance: number
  /** Total time in this bin (seconds) */
  duration: number
}

@customElement('activity-detail')
export class ActivityDetail extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      background: var(--cairn-background-color, #f8fafc);
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .container {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
    }

    .content-scroll {
      flex: 1;
      overflow: auto;
      transition: transform 0.15s ease-out;
    }

    .header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex-shrink: 0;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .back-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .title-input {
      flex: 1;
      min-width: 0;
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      padding: 4px 8px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 6px;
      background: var(--cairn-surface-color, #ffffff);
    }

    .plan-picker {
      padding: 6px 8px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 6px;
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      font-size: var(--cairn-text-size-sm, 14px);
      max-width: 100%;
    }

    .gap-prompt {
      display: flex;
      flex-direction: column;
      gap: 8px;
      background: #fef3c7;
      border: 1px solid #f59e0b;
      border-radius: 8px;
      padding: 12px;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .gap-prompt-text {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      font-size: var(--cairn-text-size-sm, 14px);
      color: #1e293b;
    }

    .gap-prompt-text .material-icons {
      color: #d97706;
      flex-shrink: 0;
    }

    .gap-prompt-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .gap-btn {
      padding: 6px 12px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 6px;
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      cursor: pointer;
      font-size: 13px;
    }

    .gap-btn.primary {
      background: var(--cairn-primary-color, #3b82f6);
      color: #ffffff;
      border-color: transparent;
    }

    .edit-track-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    .hint {
      font-size: var(--cairn-text-size-sm, 13px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 4px 0 8px;
    }

    .split-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .sync-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      flex-shrink: 0;
    }

    .sync-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .sync-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .sync-button.pushing .material-icons {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .content {
      flex: 1;
      padding: var(--cairn-spacing-lg, 24px);
      max-width: 900px;
      width: 100%;
      box-sizing: border-box;
    }

    .meta-row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cairn-spacing-md, 16px);
      align-items: center;
      margin-bottom: var(--cairn-spacing-md, 16px);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .meta-row .meta {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .meta-row .material-icons {
      font-size: 16px;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      display: inline-block;
    }

    .section {
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .section-title {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
      padding-bottom: var(--cairn-spacing-xs, 4px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: var(--cairn-spacing-md, 16px);
    }

    .stat {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
    }

    .stat-label {
      font-size: 12px;
      text-transform: uppercase;
      color: var(--cairn-text-secondary-color, #64748b);
      letter-spacing: 0.04em;
      margin-bottom: 4px;
    }

    .stat-value {
      font-size: 20px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
    }

    .chart {
      height: 160px;
    }

    .charts-stack > .chart + .chart {
      margin-top: 1px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--cairn-text-size-sm, 14px);
    }

    th,
    td {
      text-align: left;
      padding: 8px 12px;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    th {
      font-weight: 600;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    td.num {
      font-variant-numeric: tabular-nums;
    }

    .empty {
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
      padding: var(--cairn-spacing-sm, 8px) 0;
    }

    .related-row {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .related-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: var(--cairn-text-size-sm, 14px);
      padding: 4px 10px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-color, #1e293b);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      font-family: inherit;
    }

    .related-chip:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .related-chip .material-icons {
      font-size: 14px;
    }

    .toggle-row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cairn-spacing-md, 16px);
      margin-bottom: var(--cairn-spacing-md, 16px);
      align-items: flex-start;
    }

    .switch-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .switch-group label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      font-size: 13px;
      color: var(--cairn-text-color, #1e293b);
      min-width: 160px;
    }

    .toggle-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .toggle-group label {
      font-size: 12px;
      text-transform: uppercase;
      color: var(--cairn-text-secondary-color, #64748b);
      letter-spacing: 0.04em;
      margin-right: 4px;
    }

    .toggle-btn {
      font-size: 12px;
      font-family: inherit;
      padding: 4px 10px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      transition: all 0.15s;
    }

    .toggle-btn:first-of-type {
      border-radius: 6px 0 0 6px;
    }

    .toggle-btn:last-of-type {
      border-radius: 0 6px 6px 0;
    }

    .toggle-btn.active {
      background: var(--cairn-primary-color, #3b82f6);
      border-color: var(--cairn-primary-color, #3b82f6);
      color: #ffffff;
    }

    .toggle-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .legs-table td.leg-rest {
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
    }

    .legs-table td.leg-vehicle {
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
    }

    .legs-table td.leg-interp {
      color: #d97706;
      font-style: italic;
    }

    tr.split-interp {
      color: #d97706;
      font-style: italic;
    }

    /* Inline per-leg elevation gain/loss — matches the chart stats colors. */
    .leg-elev {
      display: inline-flex;
      gap: 6px;
      margin-left: 8px;
      font-size: 0.85em;
      white-space: nowrap;
    }
    .leg-gain {
      color: #4ade80;
    }
    .leg-loss {
      color: #f87171;
    }
    /* Set the arrow apart from the leading digit (↑1 reads as "11"). */
    .leg-elev .dir-arrow {
      font-weight: 700;
      margin-right: 3px;
    }

    @media (min-width: 769px) {
      .header {
        display: none;
      }
    }
  `

  @property({ type: String }) activityId?: string
  @state() private activity: Activity | null = null
  @state() private syncing = false
  @state() private pushing = false
  @state() private trackFeature: Feature | null = null
  @state() private linkedPlan: Plan | null = null
  @state() private followedRoute: Route | null = null
  @state() private editingName = false
  @state() private nameDraft = ''
  @state() private showPlanPicker = false
  @state() private allPlans: Plan[] = []
  @state() private gapPromptDismissed = false
  @state() private editingTrack = false
  @state() private processedTrack: ProcessedTrack | null = null
  /** Full-track DEM elevations aligned 1:1 with processedTrack.smoothedCoords /
   *  segments (the assembled track). Stored so per-leg gain/loss can index by
   *  segment index — cleanedDemElevations is the rests-removed subset and can't. */
  private fullDemElevations: (number | null)[] = []
  private cachedCorridor: GeoJSON.Position[][][] = []
  @state() private excludeDrives = true
  /** 'cleaned' = smoothed/rests-removed track; 'full' = every raw GPS fix */
  @state() private trackView: 'cleaned' | 'full' = 'cleaned'
  @state() private elevationSource: 'gps' | 'dem' = 'gps'
  @state() private demAvailable = false

  private settingsStorage = new SettingsStorage()

  private get units(): UnitSystem {
    return this.settingsStorage.getSettingsWithDefaults().unitSystem
  }

  override connectedCallback(): void {
    super.connectedCallback()
    if (this.activityId) {
      void this.loadActivity()
      this.syncing = true
      void syncChildrenOnNavigate('activity', this.activityId)
        .then(() => this.loadActivity())
        .finally(() => {
          this.syncing = false
        })
    }
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('activityId') && this.activityId) void this.loadActivity()
  }

  private async loadActivity(): Promise<void> {
    if (!this.activityId) return
    this.activity = (await db.activities.get(this.activityId)) ?? null
    if (this.activity) {
      const features = await featureStorage.getFeaturesForEntity('activity', this.activityId)
      this.trackFeature =
        features.find(f => f.type === 'track' && f.geometry.type === 'LineString') ?? null
      // Migrate any legacy fill points that were written into the raw geometry
      // back out (restores the recording); fills now live in an overlay.
      if (this.trackFeature && (await stripLegacyInterpolation(this.trackFeature))) {
        this.trackFeature = (await db.features.get(this.trackFeature.id)) ?? null
      }
      this.runTrackProcessing()
      await this.loadRelated()
      if (!this.activity.name) {
        void this.tryGeocodeActivityName()
      }
    } else {
      this.trackFeature = null
      this.linkedPlan = null
      this.followedRoute = null
      this.processedTrack = null
    }
  }

  private async tryGeocodeActivityName(): Promise<void> {
    if (!this.activity || !this.activityId) return
    const coords = this.trackCoords
    if (coords.length === 0) return

    const [lng, lat] = coords[0]
    try {
      const geo = await reverseGeocodeService.lookup(lat, lng)
      if (!geo?.displayName) return

      const distance = this.totalDistance
      const distStr = convertDistance(distance, this.units)
      const name = `${geo.displayName} (${distStr})`

      await activityStorage.updateActivity(this.activityId, { name })
      this.activity = { ...this.activity, name }
    } catch {
      // Still offline — will retry next time
    }
  }

  private formatDateTime(dateString?: string): string {
    if (!dateString) return 'Activity'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  private runTrackProcessing(): void {
    if (this.trackFeature?.geometry.type !== 'LineString') {
      this.processedTrack = null
      return
    }

    const props = (this.trackFeature.properties ?? {}) as Record<string, unknown>
    const coordAccuracies = props.coordAccuracies as (number | null)[] | undefined
    const coordActivities = props.coordActivities as (string | null)[] | undefined

    if (!coordAccuracies || !coordActivities) {
      this.processedTrack = null
      this.demAvailable = false
      return
    }

    // Assemble raw + gap-fill OVERLAY (raw geometry is never mutated). The
    // cleaned track is computed from this; the "Full" view stays pure raw.
    const t = assembleTrack(this.trackFeature)

    this.processedTrack = processTrack(
      t.coords,
      t.coordTimes,
      t.coordAccuracies,
      t.coordActivities,
      t.coordSpeeds,
      t.coordDemElevations,
      {},
      t.interpolated
    )
    this.fullDemElevations = t.coordDemElevations
    this.demAvailable = t.coordDemElevations.some(e => e !== null)

    // Default toggles
    const hasVehicle = this.processedTrack.tags.includes('vehicle')
    this.excludeDrives = hasVehicle

    // If cleaning removed everything (e.g. all rests/stationary), default to the
    // full track so there's something to show instead of an empty state.
    this.trackView = this.processedTrack.cleanedCoords.length >= 2 ? 'cleaned' : 'full'

    // Default elevation source based on availability
    this.elevationSource = this.demAvailable ? 'dem' : 'gps'

    this.computeCorridor()
  }

  private async loadRelated(): Promise<void> {
    if (!this.activityId) return
    const [planId, routeId] = await Promise.all([
      activityStorage.getLinkedPlanId(this.activityId),
      activityStorage.getFollowedRouteId(this.activityId),
    ])
    this.linkedPlan = planId ? ((await db.plans.get(planId)) ?? null) : null
    this.followedRoute = routeId ? ((await db.routes.get(routeId)) ?? null) : null
  }

  // --- Rename ---

  private startEditName = (): void => {
    this.nameDraft = this.activity?.name ?? ''
    this.editingName = true
  }

  private saveName = async (): Promise<void> => {
    if (!this.activityId || !this.activity) return
    const name = this.nameDraft.trim()
    // A typed name sets userNamed so auto-naming (plan/geocode) never clobbers
    // it. Clearing it reverts to automatic naming.
    await activityStorage.updateActivity(
      this.activityId,
      name ? { name, userNamed: true } : { name: undefined, userNamed: false }
    )
    this.activity = { ...this.activity, name: name || undefined, userNamed: !!name }
    this.editingName = false
  }

  // --- Manual plan association ---

  private openPlanPicker = async (): Promise<void> => {
    this.allPlans = (await db.plans.toArray()).sort((a, b) => a.name.localeCompare(b.name))
    this.showPlanPicker = true
  }

  private handlePickPlan = async (e: Event): Promise<void> => {
    const planId = (e.target as HTMLSelectElement).value
    this.showPlanPicker = false
    if (!planId || !this.activityId) return
    await associateActivityWithPlan(this.activityId, planId)
    await this.loadActivity()
  }

  private handleUnlinkPlan = async (): Promise<void> => {
    if (!this.activityId || !this.linkedPlan) return
    await unassociateActivityFromPlan(this.activityId, this.linkedPlan.id)
    await this.loadActivity()
  }

  // --- Track gaps / corrections ---

  /** Significant GPS gaps in the raw track that have NOT been filled. */
  private get trackGaps(): TrackGap[] {
    const times = this.allCoordTimes
    if (!times) return []
    const filled = this.trackFeature ? filledGapStartIndices(this.trackFeature) : new Set<number>()
    return detectTrackGaps(this.allCoords as number[][], times).filter(g => !filled.has(g.startIdx))
  }

  private get gapsAcknowledged(): boolean {
    return Boolean(
      (this.trackFeature?.properties as { gapsAcknowledged?: boolean } | undefined)
        ?.gapsAcknowledged
    )
  }

  /** Polylines (one per gap) for the dashed "no GPS here" map segment. */
  private get gapSegments(): GeoJSON.Position[][] {
    const coords = this.allCoords
    return this.trackGaps.map(g => [coords[g.startIdx], coords[g.endIdx]])
  }

  /** All of the plan's route LineString segments (the route is often split). */
  private async planRouteLines(): Promise<number[][][] | undefined> {
    if (!this.linkedPlan) return undefined
    const feats = await featureStorage.getFeaturesForEntity('plan', this.linkedPlan.id)
    const lines = feats
      .filter(f => f.geometry?.type === 'LineString')
      .map(f => (f.geometry as GeoJSON.LineString).coordinates as number[][])
    return lines.length ? lines : undefined
  }

  private handleFillGap = async (gap: TrackGap): Promise<void> => {
    if (!this.trackFeature) return
    await fillGap(this.trackFeature, gap, await this.planRouteLines())
    this.gapPromptDismissed = true
    await this.loadActivity()
  }

  private handleSplitAtGap = async (gap: TrackGap): Promise<void> => {
    if (!this.activityId || !this.trackFeature) return
    const { newActivityId } = await splitTrack(this.activityId, this.trackFeature, gap.toMs)
    router.navigate(`/activities/${newActivityId}`)
  }

  private handleLeaveGap = async (): Promise<void> => {
    if (this.trackFeature) {
      // Persist so we don't re-prompt; preserve all existing parallel arrays.
      await featureStorage.updateFeature(this.trackFeature.id, {
        properties: { ...(this.trackFeature.properties ?? {}), gapsAcknowledged: true },
      })
    }
    this.gapPromptDismissed = true
    await this.loadActivity()
  }

  /** Rests long enough to be worth splitting on (e.g. back at camp) → the index
   *  where movement resumes after the rest. */
  private get restSplitOptions(): { label: string; splitTimeMs: number }[] {
    const rests = this.processedTrack?.restSegments ?? []
    const times = this.allCoordTimes
    if (!times) return []
    const n = times.length
    return rests
      .filter(r => r.endTime - r.startTime >= 5 * 60 * 1000)
      .map(r => {
        // Split (by timestamp) where movement resumes after the rest.
        const splitIdx = times.findIndex(t => t > r.endTime)
        const mins = Math.round((r.endTime - r.startTime) / 60000)
        const at = new Date(r.startTime).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        })
        return { label: `${mins} min rest at ${at}`, splitTimeMs: r.endTime, splitIdx }
      })
      .filter(o => o.splitIdx >= 2 && o.splitIdx <= n - 2)
      .map(o => ({ label: o.label, splitTimeMs: o.splitTimeMs }))
  }

  private handleSplitAt = async (splitTimeMs: number): Promise<void> => {
    if (!this.activityId || !this.trackFeature) return
    const { newActivityId } = await splitTrack(this.activityId, this.trackFeature, splitTimeMs)
    router.navigate(`/activities/${newActivityId}`)
  }

  // --- Computed track data ---

  /** All coordinates from the track LineString — [lng, lat, ele?] */
  private get allCoords(): GeoJSON.Position[] {
    if (this.trackFeature?.geometry.type !== 'LineString') return []
    return (this.trackFeature.geometry as GeoJSON.LineString).coordinates
  }

  /** Coordinates for display and metrics. 'full' view shows every raw fix;
   *  'cleaned' uses the smoothed/rests-removed track when available. */
  private get trackCoords(): GeoJSON.Position[] {
    if (this.trackView === 'full') return this.allCoords
    if (this.processedTrack) return this.processedTrack.cleanedCoords
    return this.allCoords
  }

  /** Raw per-coordinate DEM elevations from the feature (aligned to allCoords) */
  private get allDemElevations(): (number | null)[] {
    const d = (this.trackFeature?.properties as { coordDemElevations?: unknown } | undefined)
      ?.coordDemElevations
    return Array.isArray(d) ? (d as (number | null)[]) : this.allCoords.map(() => null)
  }

  /** Interpolated (gap-fill) flag aligned with trackCoords for the current view. */
  private get interpolatedFlags(): boolean[] {
    if (this.trackView === 'full') {
      const v = (this.trackFeature?.properties as { coordInterpolated?: unknown } | undefined)
        ?.coordInterpolated
      return Array.isArray(v) ? (v as boolean[]) : this.allCoords.map(() => false)
    }
    return this.processedTrack?.cleanedInterpolated ?? this.trackCoords.map(() => false)
  }

  /** Rest segment midpoints for map display */
  private get restMidpoints(): [number, number][] {
    return this.processedTrack?.restSegments.map(r => r.midpoint) ?? []
  }

  private computeCorridor(): void {
    if (!this.processedTrack || this.processedTrack.displayCoords.length < 2) {
      this.cachedCorridor = []
      return
    }
    // Corridor uses the sparse display centerline + per-bin scatter: it's a fuzzy
    // band, and unioning a polygon per cleaned point (~2.4k) is ~20× slower for no
    // visible difference (the band is far wider than the ≤7 m line offset).
    this.cachedCorridor = computeUncertaintyCorridor(
      this.processedTrack.displayCoords,
      this.processedTrack.displayScatter
    )
  }

  /** Render-only line: the cleaned (Kalman) track in cleaned view, raw in full
   *  view. Stats also use cleanedCoords. */
  private get displayTrackCoords(): GeoJSON.Position[] {
    if (this.trackView === 'full') return this.allCoords
    if (this.processedTrack) return this.processedTrack.cleanedCoords
    return this.allCoords
  }

  /** All per-coordinate timestamps (ms epoch) */
  private get allCoordTimes(): number[] | null {
    const t = (this.trackFeature?.properties as { coordTimes?: unknown } | undefined)?.coordTimes
    return Array.isArray(t) && t.every(v => typeof v === 'number') ? t : null
  }

  /** Timestamps aligned with trackCoords */
  private get coordTimes(): number[] | null {
    if (this.trackView === 'full') return this.allCoordTimes
    if (this.processedTrack) return this.processedTrack.cleanedTimestamps
    return this.allCoordTimes
  }

  /** Cumulative distance at each coord index, in meters */
  private get cumDistances(): number[] {
    const coords = this.trackCoords
    const result: number[] = new Array(coords.length).fill(0) as number[]
    for (let i = 1; i < coords.length; i++) {
      const seg = ActivityDetail.haversineM(coords[i - 1], coords[i])
      // Honest distance: a single segment longer than the gap threshold is a
      // tracking-loss straight-line jump, never real travel on foot — don't
      // count it. (Interpolated fills are short segments and still count.)
      result[i] = result[i - 1] + (seg > DEFAULT_MIN_GAP_METERS ? 0 : seg)
    }
    return result
  }

  private get totalDistance(): number {
    const cd = this.cumDistances
    return cd.length > 0 ? cd[cd.length - 1] : 0
  }

  /** Elevations for the current elevationSource, aligned to trackCoords */
  private get currentElevations(): (number | null)[] {
    if (this.elevationSource === 'dem') {
      if (this.trackView === 'full') return this.allDemElevations
      if (this.processedTrack) return this.processedTrack.cleanedDemElevations
    }
    // GPS elevation from Z coordinate of the displayed track
    return this.trackCoords.map(c => (c.length >= 3 ? c[2] : null))
  }

  private get elevationStats(): { gain: number; loss: number; max: number; min: number } {
    return computeElevationStats(this.currentElevations, 3)
  }

  /** Total elapsed duration in seconds */
  private get totalDuration(): number {
    const ct = this.coordTimes
    if (ct && ct.length >= 2) return (ct[ct.length - 1] - ct[0]) / 1000
    if (this.activity?.startTime && this.activity.endTime) {
      return (
        (new Date(this.activity.endTime).getTime() - new Date(this.activity.startTime).getTime()) /
        1000
      )
    }
    return 0
  }

  /** Moving time (excludes rests) in seconds */
  private get movingDuration(): number | null {
    if (!this.processedTrack) return null
    return this.processedTrack.movingTimeMs / 1000
  }

  private get elevationProfile(): ProfilePoint[] {
    return computeElevationProfile(this.cumDistances, this.currentElevations)
  }

  /** Named profiles for overlay comparison */
  private get elevationProfiles(): NamedProfile[] {
    const coords = this.trackCoords
    const cd = this.cumDistances

    const gpsElevations = coords.map(c => (c.length >= 3 ? c[2] : null))
    const profiles: NamedProfile[] = [
      { name: 'GPS', points: computeElevationProfile(cd, gpsElevations), color: '#ff4444' },
    ]

    if (this.demAvailable) {
      const demEle =
        this.trackView === 'full'
          ? this.allDemElevations
          : (this.processedTrack?.cleanedDemElevations ?? [])
      profiles.push({
        name: 'Terrain',
        points: computeElevationProfile(cd, demEle),
        color: '#22c55e',
      })
    }

    return profiles
  }

  private get distanceTimeData(): DistanceTimePoint[] {
    const ct = this.coordTimes
    if (!ct || ct.length === 0) return []
    const cd = this.cumDistances
    const t0 = ct[0]
    return ct.map((t, i) => ({ elapsed: (t - t0) / 1000, distance: cd[i] }))
  }

  private get restPeriodsData(): RestPeriod[] {
    if (!this.processedTrack) return []
    const ct = this.coordTimes
    if (!ct || ct.length === 0) return []
    const t0 = ct[0]
    return this.processedTrack.restSegments.map(r => ({
      start: (r.startTime - t0) / 1000,
      end: (r.endTime - t0) / 1000,
    }))
  }

  /** Whether track analytics controls should be enabled */
  private get hasProcessingData(): boolean {
    return this.processedTrack !== null
  }

  /** Pace splits at each whole mile (or km in metric mode) */
  private get paceSplits(): PaceSplit[] {
    const ct = this.coordTimes
    if (!ct || ct.length < 2) return []
    const coords = this.trackCoords
    const cd = this.cumDistances
    const interp = this.interpolatedFlags
    const isMetric = this.units === 'metric'
    const splitMeters = isMetric ? 1000 : 1609.34

    const splits: PaceSplit[] = []
    let splitIdx = 0
    // Anchor marks the start of the current in-progress split
    let anchorDist = 0
    let anchorTime = ct[0]
    let splitGain = 0
    let splitInterpDist = 0

    for (let i = 1; i < coords.length; i++) {
      const dStart = cd[i - 1]
      const dEnd = cd[i]
      const tStart = ct[i - 1]
      const tEnd = ct[i]
      const segDist = dEnd - dStart
      const segDt = tEnd - tStart
      const segInterp = interp[i] || interp[i - 1]
      const segGain =
        coords[i].length >= 3 && coords[i - 1].length >= 3
          ? Math.max(0, coords[i][2] - coords[i - 1][2])
          : 0

      // The portion of this segment that contributes to the in-progress split
      // grows as we walk past split boundaries within the segment.
      let segCursor = dStart // cumulative distance of the last boundary we crossed (or dStart)

      // Cross zero or more split boundaries within this segment
      for (;;) {
        const targetEnd = (splitIdx + 1) * splitMeters
        if (dEnd < targetEnd || segDist <= 0) break

        // Fraction of *the whole segment* at which the boundary lies
        const fracOfSeg = (targetEnd - dStart) / segDist
        const tBoundary = tStart + fracOfSeg * segDt
        // Gain for the slice of this segment from segCursor to targetEnd
        const sliceDist = targetEnd - segCursor
        const sliceFrac = sliceDist / segDist
        const sliceGain = segGain * sliceFrac
        const splitDist = targetEnd - anchorDist
        const interpDist = splitInterpDist + (segInterp ? sliceDist : 0)

        splits.push({
          index: splitIdx,
          distance: splitDist,
          duration: (tBoundary - anchorTime) / 1000,
          gain: splitGain + sliceGain,
          interpolated: splitDist > 0 && interpDist >= splitDist * 0.5,
        })

        anchorDist = targetEnd
        anchorTime = tBoundary
        splitGain = 0
        splitInterpDist = 0
        splitIdx++
        segCursor = targetEnd
      }

      // Residual of this segment after any boundaries belongs to the in-progress split
      if (segCursor < dEnd && segDist > 0) {
        const residualDist = dEnd - segCursor
        splitGain += segGain * (residualDist / segDist)
        if (segInterp) splitInterpDist += residualDist
      }
    }

    // Final partial split
    const lastDist = cd[cd.length - 1]
    if (lastDist > anchorDist) {
      const splitDist = lastDist - anchorDist
      splits.push({
        index: splitIdx,
        distance: splitDist,
        duration: (ct[ct.length - 1] - anchorTime) / 1000,
        gain: splitGain,
        interpolated: splitDist > 0 && splitInterpDist >= splitDist * 0.5,
      })
    }

    return splits
  }

  /** Distance + time bucketed by slope angle */
  private get slopeBins(): SlopeBin[] {
    const ct = this.coordTimes
    if (!ct || ct.length < 2) return []
    const coords = this.trackCoords
    const cd = this.cumDistances

    // Bins: descents and ascents at increasing severity
    const bins: { label: string; min: number; max: number }[] = [
      { label: 'Steep down', min: -Infinity, max: -15 },
      { label: 'Moderate down', min: -15, max: -5 },
      { label: 'Flat', min: -5, max: 5 },
      { label: 'Moderate up', min: 5, max: 15 },
      { label: 'Steep up', min: 15, max: Infinity },
    ]
    const out: SlopeBin[] = bins.map(b => ({ label: b.label, distance: 0, duration: 0 }))
    const interp = this.interpolatedFlags

    for (let i = 1; i < coords.length; i++) {
      // Pace-by-slope is a MEASURED statistic — never count synthetic gap-fill.
      if (interp[i] || interp[i - 1]) continue
      const segDist = cd[i] - cd[i - 1]
      if (segDist === 0) continue
      if (coords[i].length < 3 || coords[i - 1].length < 3) continue
      const dz = coords[i][2] - coords[i - 1][2]
      const grade = (dz / segDist) * 100
      const segDuration = (ct[i] - ct[i - 1]) / 1000
      for (let bi = 0; bi < bins.length; bi++) {
        if (grade >= bins[bi].min && grade < bins[bi].max) {
          out[bi].distance += segDist
          out[bi].duration += segDuration
          break
        }
      }
    }

    return out.filter(b => b.distance > 0)
  }

  // --- Helpers ---

  private static haversineM(a: GeoJSON.Position, b: GeoJSON.Position): number {
    const toRad = (d: number): number => (d * Math.PI) / 180
    const R = 6371000
    const dLat = toRad(b[1] - a[1])
    const dLon = toRad(b[0] - a[0])
    const lat1 = toRad(a[1])
    const lat2 = toRad(b[1])
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
  }

  private formatDuration(seconds: number): string {
    if (!isFinite(seconds) || seconds <= 0) return '—'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    if (h > 0) return `${h}h ${m}m`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }

  /** Pace as time per unit distance (mile or km) */
  private formatPace(meters: number, seconds: number): string {
    // `!(x > 0)` also rejects NaN/Infinity, not just <= 0
    if (!(meters > 0) || !(seconds > 0)) return '—'
    const isMetric = this.units === 'metric'
    const unitMeters = isMetric ? 1000 : 1609.34
    const secPerUnit = (seconds / meters) * unitMeters
    if (!Number.isFinite(secPerUnit)) return '—'
    // Round total seconds first so 59.6 → 1:00, never 0:60
    const total = Math.round(secPerUnit)
    const m = Math.floor(total / 60)
    const s = total % 60
    return `${m}:${String(s).padStart(2, '0')}/${isMetric ? 'km' : 'mi'}`
  }

  // --- Chart cursor sync ---

  private get elevTimeChart(): ElevationProfile | undefined {
    return this.shadowRoot?.querySelector('elevation-profile:not(.elev-dist)') as
      | ElevationProfile
      | undefined
  }

  private get elevDistChart(): ElevationProfile | undefined {
    return this.shadowRoot?.querySelector('elevation-profile.elev-dist') as
      | ElevationProfile
      | undefined
  }

  private get dtChart(): DistanceTimeChart | undefined {
    return this.shadowRoot?.querySelector('distance-time-chart') as DistanceTimeChart | undefined
  }

  private handleElevCursor(e: CustomEvent<{ elapsed: number | null }>): void {
    this.dtChart?.setCursorElapsed(e.detail.elapsed)
    this.elevDistChart?.setCursorElapsed(e.detail.elapsed)
  }

  private handleDtCursor(e: CustomEvent<{ elapsed: number | null }>): void {
    this.elevTimeChart?.setCursorElapsed(e.detail.elapsed)
    this.elevDistChart?.setCursorElapsed(e.detail.elapsed)
  }

  private handleElevDistCursor(e: CustomEvent<{ elapsed: number | null }>): void {
    this.elevTimeChart?.setCursorElapsed(e.detail.elapsed)
    this.dtChart?.setCursorElapsed(e.detail.elapsed)
  }

  private handleElevZoom(e: CustomEvent<{ min: number | null; max: number | null }>): void {
    this.elevDistChart?.setZoomRange(e.detail.min, e.detail.max)
    this.dtChart?.setZoomRange(e.detail.min, e.detail.max)
  }

  private handleDtZoom(e: CustomEvent<{ min: number | null; max: number | null }>): void {
    this.elevDistChart?.setZoomRange(e.detail.min, e.detail.max)
    this.elevTimeChart?.setZoomRange(e.detail.min, e.detail.max)
  }

  private handleElevDistZoom(e: CustomEvent<{ min: number | null; max: number | null }>): void {
    this.elevTimeChart?.setZoomRange(e.detail.min, e.detail.max)
    this.dtChart?.setZoomRange(e.detail.min, e.detail.max)
  }

  // --- Pull-to-refresh ---
  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async handlePushSync(): Promise<void> {
    if (!this.activityId) return
    this.pushing = true
    try {
      await forcePushEntity('activity', this.activityId)
    } finally {
      this.pushing = false
    }
  }

  private async handleDelete(): Promise<void> {
    if (!this.activity) return
    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Delete Activity'
      dialog.message = `Delete "${this.activity.name ?? 'this activity'}"? This will also delete associated track data and cannot be undone.`
      const confirmed = await dialog.show()
      if (!confirmed) return
    }
    await activityStorage.deleteActivity(this.activity.id)
    router.navigate('/activities')
  }

  private async refresh(): Promise<void> {
    if (!this.activityId) return
    this.syncing = true
    try {
      await syncChildrenOnNavigate('activity', this.activityId)
      await this.loadActivity()
    } finally {
      this.syncing = false
    }
  }

  private renderTrackEdit(): unknown {
    const opts = this.restSplitOptions
    return html`
      <div class="section">
        <div class="edit-track-head">
          <h2 class="section-title">Edit track</h2>
          <button
            class="gap-btn"
            @click=${() => {
              this.editingTrack = !this.editingTrack
            }}
          >
            ${this.editingTrack ? 'Done' : 'Split into activities'}
          </button>
        </div>
        ${this.editingTrack
          ? opts.length > 0
            ? html`
                <p class="hint">
                  Split a long rest into its own activity (e.g. before/after camp). The part after
                  the rest becomes a new activity.
                </p>
                ${opts.map(
                  o => html`
                    <div class="split-row">
                      <span>${o.label}</span>
                      <button
                        class="gap-btn primary"
                        @click=${() => this.handleSplitAt(o.splitTimeMs)}
                      >
                        Split here
                      </button>
                    </div>
                  `
                )}
              `
            : html`<p class="hint">No long rests detected to split on.</p>`
          : nothing}
      </div>
    `
  }

  private renderGapPrompt(): unknown {
    const gaps = this.trackGaps
    if (gaps.length === 0 || this.gapsAcknowledged || this.gapPromptDismissed) return nothing
    const gap = gaps.reduce((a, b) => (b.meters > a.meters ? b : a))
    const dist =
      gap.meters >= 1000 ? `${(gap.meters / 1000).toFixed(1)} km` : `${Math.round(gap.meters)} m`
    const mins = Math.round((gap.toMs - gap.fromMs) / 60000)
    const fmt = (ms: number): string =>
      new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    return html`
      <div class="gap-prompt">
        <div class="gap-prompt-text">
          <span class="material-icons">warning</span>
          <span>
            Looks like a tracking issue from ${fmt(gap.fromMs)} to ${fmt(gap.toMs)} (${dist},
            ${mins} min)${gaps.length > 1 ? `, and ${gaps.length - 1} more` : ''}. How should it be
            handled?
          </span>
        </div>
        <div class="gap-prompt-actions">
          <button class="gap-btn primary" @click=${() => this.handleFillGap(gap)}>
            ${this.linkedPlan ? 'Fill from plan' : 'Fill (straight line)'}
          </button>
          <button class="gap-btn" @click=${() => this.handleSplitAtGap(gap)}>Split here</button>
          <button class="gap-btn" @click=${this.handleLeaveGap}>Leave as-is</button>
        </div>
      </div>
    `
  }

  /** Gain/loss (meters) over a leg's full-track index range, using the active
   *  elevation source (DEM when available — a clean lookup — else smoothed GPS).
   *  Shown per leg so the vertical work is visible: a 40-min leg that covers only
   *  179 horizontal ft reads sensibly once you see it climbed +100 ft. */
  private legElevation(startIndex: number, endIndex: number): { gain: number; loss: number } {
    const src =
      this.elevationSource === 'dem' && this.demAvailable
        ? this.fullDemElevations
        : (this.processedTrack?.smoothedCoords.map(c => (c.length >= 3 ? c[2] : null)) ?? [])
    return computeElevationStats(src.slice(startIndex, endIndex + 1), 3)
  }

  private renderSegmentRows(): unknown {
    if (!this.processedTrack) return nothing
    const coords = this.processedTrack.smoothedCoords
    // Times aligned 1:1 with segments/smoothedCoords (assembled track, incl.
    // interpolated fills) — NOT the raw feature times, which omit fill points.
    const allTimes = this.processedTrack.timestamps
    let legNum = 0

    return this.processedTrack.segments.map(seg => {
      if (seg.type === 'vehicle') {
        return html`<tr>
          <td class="leg-vehicle">—</td>
          <td class="leg-vehicle" colspan="3">Drive</td>
        </tr>`
      }
      if (seg.type === 'rest') {
        const dur = allTimes ? (allTimes[seg.endIndex] - allTimes[seg.startIndex]) / 1000 : 0
        // Preserved activity-recognition aggregate: characterizes the rest (sat
        // still vs milled in place) without being used to detect it.
        const rest = this.processedTrack?.restSegments.find(r => r.startIndex === seg.startIndex)
        const stillPct =
          rest?.stationaryFraction != null ? Math.round(rest.stationaryFraction * 100) : null
        const kind = stillPct == null ? '' : stillPct >= 50 ? ' · mostly still' : ' · moved around'
        return html`<tr>
          <td class="leg-rest">—</td>
          <td class="leg-rest" colspan="3">Rested ${this.formatDuration(dur)}${kind}</td>
        </tr>`
      }
      if (seg.type === 'interpolated') {
        let d = 0
        for (let i = seg.startIndex + 1; i <= seg.endIndex; i++) {
          d += haversineDistance(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0])
        }
        const dur = allTimes ? (allTimes[seg.endIndex] - allTimes[seg.startIndex]) / 1000 : 0
        return html`<tr>
          <td class="leg-interp">~</td>
          <td class="leg-interp" colspan="3">
            Interpolated ${convertDistance(d, this.units)} · ${this.formatPace(d, dur)} est.
          </td>
        </tr>`
      }

      // Skip a degenerate single-point "leg" (a lone moving fix wedged between
      // two rests) — it has zero distance and zero duration.
      if (seg.endIndex <= seg.startIndex) return nothing

      legNum++
      let dist = 0
      for (let i = seg.startIndex; i <= seg.endIndex; i++) {
        if (i > seg.startIndex) {
          dist += haversineDistance(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0])
        }
      }
      const dur = allTimes ? (allTimes[seg.endIndex] - allTimes[seg.startIndex]) / 1000 : 0
      const { gain, loss } = this.legElevation(seg.startIndex, seg.endIndex)

      return html`<tr>
        <td class="num">${legNum}</td>
        <td class="num">
          ${convertDistance(dist, this.units)}
          <span class="leg-elev">
            <span class="leg-gain"
              ><span class="dir-arrow">↑</span>${convertElevation(gain, this.units)}</span
            >
            <span class="leg-loss"
              ><span class="dir-arrow">↓</span>${convertElevation(loss, this.units)}</span
            >
          </span>
        </td>
        <td class="num">${this.formatDuration(dur)}</td>
        <td class="num">${this.formatPace(dist, dur)}</td>
      </tr>`
    })
  }

  override render(): unknown {
    if (!this.activity) {
      return html`<div class="container">
        <div class="content"><p>Loading activity...</p></div>
      </div>`
    }

    const a = this.activity
    const hasTrack = this.trackCoords.length >= 2
    const elev = this.elevationStats
    const dist = this.totalDistance
    const dur = this.totalDuration
    const profile = this.elevationProfile
    const dtData = this.distanceTimeData
    const rests = this.restPeriodsData
    const splits = this.paceSplits
    const slopes = this.slopeBins
    const isMetric = this.units === 'metric'
    const splitUnit = isMetric ? 'km' : 'mi'

    return html`
      <div class="container">
        <div class="header">
          <button class="back-button" @click=${() => window.history.back()}>
            <span class="material-icons">arrow_back</span>
          </button>
          ${this.editingName
            ? html`
                <input
                  class="title-input"
                  .value=${this.nameDraft}
                  @input=${(e: Event) => {
                    this.nameDraft = (e.target as HTMLInputElement).value
                  }}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === 'Enter') void this.saveName()
                    else if (e.key === 'Escape') this.editingName = false
                  }}
                />
                <button class="sync-button" @click=${this.saveName} title="Save name">
                  <span class="material-icons">check</span>
                </button>
                <button
                  class="sync-button"
                  @click=${() => {
                    this.editingName = false
                  }}
                  title="Cancel"
                >
                  <span class="material-icons">close</span>
                </button>
              `
            : html`
                <h1 class="title">${a.name ?? this.formatDateTime(a.startTime)}</h1>
                <button class="sync-button" @click=${this.startEditName} title="Rename">
                  <span class="material-icons">edit</span>
                </button>
              `}
          <button
            class="sync-button ${this.pushing ? 'pushing' : ''}"
            @click=${this.handlePushSync}
            ?disabled=${this.pushing}
            title="Push to server"
          >
            <span class="material-icons">cloud_upload</span>
          </button>
          <button class="sync-button" @click=${this.handleDelete} title="Delete activity">
            <span class="material-icons">delete</span>
          </button>
        </div>
        ${this.syncing
          ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
          : nothing}
        <div
          class="content-scroll"
          @touchstart=${this.handleTouchStart}
          @touchmove=${this.handleTouchMove}
          @touchend=${this.handleTouchEnd}
          @map-expand=${() => {
            router.navigate(`/activities/${this.activityId}/map`)
          }}
        >
          <div class="content">
            <div class="meta-row">
              <span class="meta"
                ><span class="material-icons">directions_walk</span>${a.activityType}</span
              >
              ${a.startTime
                ? html`<span class="meta"
                    ><span class="material-icons">schedule</span>${new Date(
                      a.startTime
                    ).toLocaleString()}</span
                  >`
                : nothing}
              ${a.conditions
                ? html`<span class="meta"
                    ><span class="material-icons">cloud</span>${a.conditions}</span
                  >`
                : nothing}
            </div>

            <div class="related-row">
              ${this.linkedPlan
                ? html`
                    <button
                      class="related-chip"
                      @click=${() => router.navigate(`/plans/${this.linkedPlan!.id}`)}
                    >
                      <span class="material-icons">map</span>
                      Plan: ${this.linkedPlan.name}
                    </button>
                    <button
                      class="sync-button"
                      @click=${this.handleUnlinkPlan}
                      title="Remove from plan"
                    >
                      <span class="material-icons">link_off</span>
                    </button>
                  `
                : this.showPlanPicker
                  ? html`
                      <select class="plan-picker" @change=${this.handlePickPlan}>
                        <option value="">Select a plan…</option>
                        ${this.allPlans.map(p => html`<option value=${p.id}>${p.name}</option>`)}
                      </select>
                      <button
                        class="sync-button"
                        @click=${() => {
                          this.showPlanPicker = false
                        }}
                        title="Cancel"
                      >
                        <span class="material-icons">close</span>
                      </button>
                    `
                  : html`
                      <button class="related-chip" @click=${this.openPlanPicker}>
                        <span class="material-icons">add_link</span>
                        Link to plan
                      </button>
                    `}
              ${this.followedRoute
                ? html`
                    <button
                      class="related-chip"
                      @click=${() => router.navigate(`/routes/${this.followedRoute!.id}`)}
                    >
                      <span class="material-icons">alt_route</span>
                      Route: ${this.followedRoute.name}
                    </button>
                  `
                : nothing}
            </div>
            ${a.description ? html`<p>${a.description}</p>` : nothing}
            ${this.hasProcessingData
              ? html`
                  <div class="toggle-row">
                    <div class="switch-group">
                      <label>
                        <span>Exclude drives</span>
                        <md-switch
                          ?selected=${this.excludeDrives}
                          ?disabled=${!this.processedTrack?.tags.includes('vehicle')}
                          @change=${(e: Event) => {
                            this.excludeDrives = (
                              e.target as HTMLInputElement & { selected: boolean }
                            ).selected
                          }}
                        ></md-switch>
                      </label>
                    </div>
                    <div class="toggle-group">
                      <label>Elevation</label>
                      <button
                        class="toggle-btn ${this.elevationSource === 'gps' ? 'active' : ''}"
                        @click=${() => {
                          this.elevationSource = 'gps'
                        }}
                      >
                        GPS
                      </button>
                      <button
                        class="toggle-btn ${this.elevationSource === 'dem' ? 'active' : ''}"
                        ?disabled=${!this.demAvailable}
                        @click=${() => {
                          this.elevationSource = 'dem'
                        }}
                      >
                        Terrain
                      </button>
                    </div>
                    <div class="toggle-group">
                      <label>Track</label>
                      <button
                        class="toggle-btn ${this.trackView === 'cleaned' ? 'active' : ''}"
                        ?disabled=${(this.processedTrack?.cleanedCoords.length ?? 0) < 2}
                        @click=${() => {
                          this.trackView = 'cleaned'
                        }}
                      >
                        Cleaned
                      </button>
                      <button
                        class="toggle-btn ${this.trackView === 'full' ? 'active' : ''}"
                        @click=${() => {
                          this.trackView = 'full'
                        }}
                      >
                        Full
                      </button>
                    </div>
                  </div>
                `
              : nothing}
            ${this.renderGapPrompt()}
            ${hasTrack
              ? html`
                  <activity-track-map
                    .trackCoords=${this.displayTrackCoords}
                    .rawTrackCoords=${this.allCoords}
                    .uncertaintyPolygon=${this.cachedCorridor}
                    .restPoints=${this.restMidpoints}
                    .gapSegments=${this.gapSegments}
                  ></activity-track-map>

                  ${this.renderTrackEdit()}

                  <div class="section">
                    <h2 class="section-title">Stats</h2>
                    <div class="stats-grid">
                      <div class="stat">
                        <div class="stat-label">Distance</div>
                        <div class="stat-value">${convertDistance(dist, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">
                          ${this.movingDuration !== null ? 'Elapsed' : 'Time'}
                        </div>
                        <div class="stat-value">${this.formatDuration(dur)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Elevation Gain</div>
                        <div class="stat-value">${convertElevation(elev.gain, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Elevation Loss</div>
                        <div class="stat-value">${convertElevation(elev.loss, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Max Elevation</div>
                        <div class="stat-value">${convertElevation(elev.max, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Avg Pace</div>
                        <div class="stat-value">${this.formatPace(dist, dur)}</div>
                      </div>
                      ${this.movingDuration !== null
                        ? html`
                            <div class="stat">
                              <div class="stat-label">Moving Time</div>
                              <div class="stat-value">
                                ${this.formatDuration(this.movingDuration)}
                              </div>
                            </div>
                            <div class="stat">
                              <div class="stat-label">Moving Pace</div>
                              <div class="stat-value">
                                ${this.formatPace(dist, this.movingDuration)}
                              </div>
                            </div>
                          `
                        : nothing}
                    </div>
                  </div>

                  <div class="section charts-stack">
                    <h2 class="section-title">Profile</h2>
                    <div class="chart">
                      <elevation-profile
                        class="elev-dist"
                        .trackProfile=${this.demAvailable ? [] : profile}
                        .profiles=${this.demAvailable ? this.elevationProfiles : []}
                        .distanceTime=${dtData}
                        .unitSystem=${this.units}
                        @chart-cursor=${this.handleElevDistCursor}
                        @chart-zoom=${this.handleElevDistZoom}
                      ></elevation-profile>
                    </div>
                    <div class="chart">
                      <elevation-profile
                        .trackProfile=${this.demAvailable ? [] : profile}
                        .profiles=${this.demAvailable ? this.elevationProfiles : []}
                        .distanceTime=${dtData}
                        .timeAxis=${true}
                        .showStats=${false}
                        .restPeriods=${rests}
                        .unitSystem=${this.units}
                        @chart-cursor=${this.handleElevCursor}
                        @chart-zoom=${this.handleElevZoom}
                      ></elevation-profile>
                    </div>
                    ${dtData.length >= 2
                      ? html`<div class="chart">
                          <distance-time-chart
                            .points=${dtData}
                            .restPeriods=${rests}
                            .unitSystem=${this.units}
                            @chart-cursor=${this.handleDtCursor}
                            @chart-zoom=${this.handleDtZoom}
                          ></distance-time-chart>
                        </div>`
                      : nothing}
                  </div>

                  <div class="section">
                    <h2 class="section-title">Pace by ${splitUnit}</h2>
                    ${splits.length > 0
                      ? html`<table>
                          <thead>
                            <tr>
                              <th>${splitUnit.toUpperCase()}</th>
                              <th>Time</th>
                              <th>Pace</th>
                              <th>Gain</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${splits.map(
                              s =>
                                html`<tr class=${s.interpolated ? 'split-interp' : ''}>
                                  <td class="num">
                                    ${s.index + 1}${s.interpolated
                                      ? html`<span
                                          title="Estimated — this mile was reconstructed from a GPS gap"
                                        >
                                          ~</span
                                        >`
                                      : ''}
                                  </td>
                                  <td class="num">${this.formatDuration(s.duration)}</td>
                                  <td class="num">${this.formatPace(s.distance, s.duration)}</td>
                                  <td class="num">${convertElevation(s.gain, this.units)}</td>
                                </tr>`
                            )}
                          </tbody>
                        </table>`
                      : html`<div class="empty">No timing data available for splits.</div>`}
                  </div>

                  <div class="section">
                    <h2 class="section-title">Pace by Slope</h2>
                    ${slopes.length > 0
                      ? html`<table>
                          <thead>
                            <tr>
                              <th>Slope</th>
                              <th>Distance</th>
                              <th>Time</th>
                              <th>Pace</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${slopes.map(
                              b =>
                                html`<tr>
                                  <td>${b.label}</td>
                                  <td class="num">${convertDistance(b.distance, this.units)}</td>
                                  <td class="num">${this.formatDuration(b.duration)}</td>
                                  <td class="num">${this.formatPace(b.distance, b.duration)}</td>
                                </tr>`
                            )}
                          </tbody>
                        </table>`
                      : html`<div class="empty">No timing or elevation data for slope bins.</div>`}
                  </div>

                  ${this.processedTrack && this.processedTrack.segments.length > 1
                    ? html`
                        <div class="section">
                          <h2 class="section-title">Legs</h2>
                          <table class="legs-table">
                            <thead>
                              <tr>
                                <th>Leg</th>
                                <th>Distance</th>
                                <th>Duration</th>
                                <th>Pace</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${this.renderSegmentRows()}
                            </tbody>
                          </table>
                        </div>
                      `
                    : nothing}
                `
              : html`<div class="section">
                  <div class="empty">No track data recorded for this activity yet.</div>
                </div>`}
          </div>
        </div>
      </div>
      <confirm-dialog></confirm-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-detail': ActivityDetail
  }
}
